import { KirchensteuerResult, FoerderungItem, CallcenterWizardData, FoerderanspruchResult } from '@/types/callcenter';
import { hatPartner } from '@/utils/haushalt';

// ── Beitragsbemessungsgrenzen 2026 (PHP-Original) ──
const BBG_KV_PV = 69750;
const BBG_RV_AV = 101400;

// Sozialversicherungssätze 2026 (AN-Anteil)
const SATZ_KV = 0.073;
const SATZ_KV_ZUSATZ = 0.0125;
const SATZ_RV = 0.093;
const SATZ_AV = 0.013;

// Pflegeversicherung AN-Anteile nach Kinderzahl (Index = Kinder, gecappt bei 5+)
const SATZ_PV: Record<number, number> = {
  0: 0.024,
  1: 0.018,
  2: 0.0155,
  3: 0.013,
  4: 0.0105,
  5: 0.008,
};

// Einkommensteuer-Tarif 2026 (§32a EStG)
const EST_GRUNDFREIBETRAG = 12348;
const EST_ZONE2_BIS = 17799;
const EST_ZONE3_BIS = 69878;
const EST_ZONE4_BIS = 277825;
const EST_ZONE2_KOEFF_A = 914.51;
const EST_ZONE2_KOEFF_B = 1400;
const EST_ZONE3_KOEFF_A = 173.10;
const EST_ZONE3_KOEFF_B = 2397;
const EST_ZONE3_OFFSET = 1034.87;
const EST_ZONE4_SATZ = 0.42;
const EST_ZONE4_ABZUG = 11135.63;
const EST_ZONE5_SATZ = 0.45;
const EST_ZONE5_ABZUG = 19470.38;

// Kirchensteuer
const KIRCHENSTEUER_SATZ_STANDARD = 0.09;
const KIRCHENSTEUER_SATZ_BAYERN_BW = 0.08;
const PLZ_BAYERN = ['80', '81', '82', '83', '84', '85', '86', '87', '88', '89', '90', '91', '92', '93', '94', '95', '96', '97'];
const PLZ_BW = ['68', '69', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79'];

// VL
const VL_ANGESTELLT = 480;
const VL_AZUBI = 480;
const VL_BEAMTER = 79.80;

// Sparzulage (10% gemaess Gesetzesaenderung seit 2024)
const SPARZULAGE_BASIS_MAX = 400;
const SPARZULAGE_SATZ = 0.10;

// --- Sozialabgaben ---
function berechneSozialabgaben(bruttojahresgehalt: number, anzahlKinder: number) {
  const kvBasis = Math.min(bruttojahresgehalt, BBG_KV_PV);
  const rvBasis = Math.min(bruttojahresgehalt, BBG_RV_AV);

  const krankenversicherung = kvBasis * (SATZ_KV + SATZ_KV_ZUSATZ);
  const rentenversicherung = rvBasis * SATZ_RV;
  const arbeitslosenversicherung = rvBasis * SATZ_AV;

  const pvSatz = SATZ_PV[Math.min(anzahlKinder, 5)] ?? SATZ_PV[5];
  const pflegeversicherung = kvBasis * pvSatz;

  const gesamt = krankenversicherung + rentenversicherung + arbeitslosenversicherung + pflegeversicherung;

  return { krankenversicherung, rentenversicherung, arbeitslosenversicherung, pflegeversicherung, gesamt };
}

// --- Einkommensteuer (§32a EStG, 2026) ---
function berechneEinkommensteuer(zvE: number): number {
  const z = Math.floor(zvE);
  if (z <= EST_GRUNDFREIBETRAG) return 0;
  if (z <= EST_ZONE2_BIS) {
    const y = (z - EST_GRUNDFREIBETRAG) / 10000;
    return Math.floor((EST_ZONE2_KOEFF_A * y + EST_ZONE2_KOEFF_B) * y);
  }
  if (z <= EST_ZONE3_BIS) {
    const zz = (z - EST_ZONE2_BIS) / 10000;
    return Math.floor((EST_ZONE3_KOEFF_A * zz + EST_ZONE3_KOEFF_B) * zz + EST_ZONE3_OFFSET);
  }
  if (z <= EST_ZONE4_BIS) {
    return Math.floor(EST_ZONE4_SATZ * z - EST_ZONE4_ABZUG);
  }
  return Math.floor(EST_ZONE5_SATZ * z - EST_ZONE5_ABZUG);
}

function berechneGrenzsteuersatz(zvE: number): number {
  const z = Math.floor(zvE);
  if (z <= EST_GRUNDFREIBETRAG) return 0;
  if (z <= EST_ZONE2_BIS) {
    const y = (z - EST_GRUNDFREIBETRAG) / 10000;
    return ((2 * EST_ZONE2_KOEFF_A * y + EST_ZONE2_KOEFF_B) / 10000) * 100;
  }
  if (z <= EST_ZONE3_BIS) {
    const zz = (z - EST_ZONE2_BIS) / 10000;
    return ((2 * EST_ZONE3_KOEFF_A * zz + EST_ZONE3_KOEFF_B) / 10000) * 100;
  }
  if (z <= EST_ZONE4_BIS) return 42;
  return 45;
}

function kirchensteuersatzFuerPlz(plz: string): number {
  if (plz && plz.length >= 2) {
    const prefix = plz.substring(0, 2);
    if (PLZ_BAYERN.includes(prefix) || PLZ_BW.includes(prefix)) {
      return KIRCHENSTEUER_SATZ_BAYERN_BW;
    }
  }
  return KIRCHENSTEUER_SATZ_STANDARD;
}

// --- Income (iterative Brutto/Netto-Umrechnung) ---
export function calculateIncome(
  brutto: number | null,
  netto: number | null,
  anzahlKinder: number = 0,
): { brutto: number; netto: number } {
  if (brutto && netto) return { brutto: Math.round(brutto), netto: Math.round(netto) };

  if (brutto && !netto) {
    const jahresBrutto = brutto * 12;
    const soz = berechneSozialabgaben(jahresBrutto, anzahlKinder);
    const zvE = jahresBrutto - soz.gesamt;
    const steuer = berechneEinkommensteuer(zvE);
    const jahresNetto = jahresBrutto - soz.gesamt - steuer;
    return { brutto: Math.round(brutto), netto: Math.round(jahresNetto / 12) };
  }

  if (!brutto && netto) {
    let geschaetztBrutto = netto * 1.5;
    for (let i = 0; i < 10; i++) {
      const jahresBrutto = geschaetztBrutto * 12;
      const soz = berechneSozialabgaben(jahresBrutto, anzahlKinder);
      const zvE = jahresBrutto - soz.gesamt;
      const steuer = berechneEinkommensteuer(zvE);
      const berechnetNetto = (jahresBrutto - soz.gesamt - steuer) / 12;
      const diff = netto - berechnetNetto;
      if (Math.abs(diff) < 1) break;
      geschaetztBrutto += diff * 0.7;
    }
    return { brutto: Math.round(geschaetztBrutto), netto: Math.round(netto) };
  }

  return { brutto: 0, netto: 0 };
}

// --- Haushaltssituation Wert ---
// Delegiert an die eine Regel (utils/haushalt, Entscheid Alex 06.08.2026) —
// vorher eine eigene Kopie der Liste. Verhalten unveraendert: 1 = mit Partner.
export function getHaushaltssituationWert(haushaltssituation: string): number {
  return hatPartner(haushaltssituation) ? 1 : 0;
}

// --- Foerderhoehe Kinder ---
/**
 * Kindererziehungszeiten je Kind und Monat: Rentenwert 2026 42,52 € × 3
 * Erziehungsjahre (Entscheid 12.08.2026, von Alex bestaetigt 08.09.).
 * Muss mit `ChildRearingPensionCredit::MONTHLY_CREDIT_PER_CHILD` im Backend
 * uebereinstimmen — der Commit d04d0eaf7 stellte nur das Backend um, der
 * Wizard rechnete bis 12.10.2026 mit 112,80 € (Rentenwert 2023). Ein Test
 * gleicht beide ab. Der Rentenwert aendert sich jeden Juli.
 */
export const KINDERERZIEHUNG_JE_KIND_MONATLICH = 127.56;

export function berechneFoerderhoeheKinder(anzahlKinder: number): number {
  return anzahlKinder * KINDERERZIEHUNG_JE_KIND_MONATLICH * 12;
}

// --- Riester ---
export function berechneFoerderanspruchRiester(
  kinderGeburtsjahre: number[],
  haushaltssituationWert: number,
): number {
  let summe = 175;
  if (haushaltssituationWert === 1) summe += 175;
  for (const geburtsjahr of kinderGeburtsjahre) {
    if (geburtsjahr >= 2008) summe += 300;
    else summe += 185;
  }
  return summe;
}

// --- Vermoegenswirksame Leistungen ---
function vlBetragFuerBerufsstand(berufsstand: string): number {
  const map: Record<string, number> = {
    angestellt: VL_ANGESTELLT,
    auszubildender: VL_AZUBI,
    beamter: VL_BEAMTER,
  };
  return map[berufsstand.toLowerCase()] ?? 0;
}

export function berechneVermoegenswirksameLeistungen(
  berufsstand1: string,
  berufsstand2: string,
): { agBeitrag: number; sparzulage: number; gesamt: number } {
  const agBeitrag = vlBetragFuerBerufsstand(berufsstand1) + vlBetragFuerBerufsstand(berufsstand2);
  const sparzulage = agBeitrag > 0 ? Math.min(agBeitrag, SPARZULAGE_BASIS_MAX) * SPARZULAGE_SATZ : 0;
  return { agBeitrag, sparzulage, gesamt: agBeitrag + sparzulage };
}

// --- Kirchensteuer (vollständige Berechnung mit PLZ-abhängigem Satz) ---
export function berechneKirchensteuer(
  bruttojahresgehalt: number,
  anzahlKinder: number,
  plz: string = '',
): KirchensteuerResult {
  const soz = berechneSozialabgaben(bruttojahresgehalt, anzahlKinder);
  const zvE = bruttojahresgehalt - soz.gesamt;
  const steuerlast = Math.max(0, berechneEinkommensteuer(zvE));
  const durchschnittssteuersatz = zvE > 0 ? (steuerlast / zvE) * 100 : 0;
  const grenzsteuersatz = berechneGrenzsteuersatz(zvE);
  const kirchensteuer = steuerlast * kirchensteuersatzFuerPlz(plz);
  const basisrenteBeitrag = 100 * 12;
  const foerdersummeBasisrente = basisrenteBeitrag * grenzsteuersatz / 100;

  return {
    bruttojahresgehalt,
    anzahlKinder,
    krankenversicherung: soz.krankenversicherung,
    rentenversicherung: soz.rentenversicherung,
    arbeitslosenversicherung: soz.arbeitslosenversicherung,
    pflegeversicherung: soz.pflegeversicherung,
    zvE,
    steuerlast,
    durchschnittssteuersatz,
    grenzsteuersatz,
    kirchensteuer,
    foerdersummeBasisrente,
  };
}

// --- Sparvertraege ---
export function berechneAnpassungSparvertraege(
  hatSparvertraege: boolean,
  haushaltssituationWert: number,
): number {
  if (!hatSparvertraege) return 0;
  return haushaltssituationWert === 1 ? 2000 : 1000;
}

// --- Freibetraege Kinder ---
export function berechneFreibetraegeKinder(
  kinderImHaushalt: number,
  hatSparvertraegeKinder: boolean,
): number {
  if (hatSparvertraegeKinder && kinderImHaushalt > 0) return (12640 * kinderImHaushalt) * 0.26375;
  return 0;
}

// --- Wohnungsbauspraemie ---
export function berechneWohnungsBauPraemie(
  sparvertraegeDaten: { produkt: string }[],
  haushaltssituationWert: number,
): number {
  const hasBauspar = sparvertraegeDaten.some(s => s.produkt === 'bausparvertraege');
  if (hasBauspar) return haushaltssituationWert === 1 ? 140 : 70;
  return 0;
}

// --- Complete Foerderanspruch Calculation ---
export function berechneGesamtFoerderanspruch(data: CallcenterWizardData): FoerderanspruchResult {
  const hw = getHaushaltssituationWert(data.haushaltssituation);
  const items: FoerderungItem[] = [];
  let total = 0;

  // 1. Riester (§10a EStG)
  const riester = berechneFoerderanspruchRiester(data.kinderGeburtsjahre, hw);
  if (riester > 0) {
    items.push({
      id: 'riester',
      beschreibung: 'Zulage für Tilgung und Kapitalaufbau §10a EStG',
      summe: riester,
      verwendungszweck: 'Riester-Förderung für Altersvorsorge und Immobilienfinanzierung',
      popup_text: 'Die staatliche Zulage beträgt 175€ pro Person + Kinderzulagen.',
      type: 'numeric',
    });
    total += riester;
  }

  // 2. Freistellungsauftrag (§44a EStG) — Sparerpauschbetrag
  //
  // 🔥 WIRD AUFGELISTET, ABER NICHT MITGERECHNET (Decision #170 — Entscheid
  // Ünal 18.08.2026, fachlich abgenommen Alex 19.08.2026).
  // Bis dahin erhöhte ein „Ja" bei Sparverträgen den Gesamtförderanspruch um
  // 1.000 € (Single) bzw. 2.000 € (verheiratet). Das ist fachlich falsch: der
  // Sparerpauschbetrag ist ein FREIBETRAG auf Kapitalerträge, keine Förderung,
  // die jemand ausgezahlt bekommt — er wirkt nur, soweit überhaupt
  // Kapitalerträge anfallen, und dann höchstens mit dem Steuersatz darauf.
  // Als Summand machte er die Kopfzahl im Gespräch um genau diesen Betrag zu
  // hoch.
  //
  // Die Position BLEIBT in der Auflistung (Step 6), weil der Freibetrag ein
  // echter Vorteil ist, über den zu sprechen sich lohnt — nur eben keiner, den
  // man addiert.
  //
  // Kein neues Muster: `kindererziehungszeiten` (Punkt 4 weiter unten) wird
  // genauso gelistet, ohne in `total` zu zählen. `total` wächst in dieser
  // Funktion nur noch an drei Stellen — Riester, VL-Arbeitgeberbeitrag und
  // Basisrente.
  const freistellung = berechneAnpassungSparvertraege(data.sparvertraege === 'ja', hw);
  if (freistellung > 0) {
    items.push({
      id: 'freistellung',
      beschreibung: 'Freistellungsauftrag für Kapitalerträge §44a EStG',
      summe: freistellung,
      verwendungszweck: 'Steuerfreie Kapitalerträge bis zum Sparerpauschbetrag',
      popup_text: 'Der Sparerpauschbetrag beträgt 1.000€ (Single) bzw. 2.000€ (Verheiratet).',
      type: 'numeric',
    });
  }

  // 3. Freibetraege Kinder
  const freibetraegeKinder = berechneFreibetraegeKinder(
    data.kinderImHaushalt,
    data.sparvertraegeKinder === 'ja',
  );
  if (freibetraegeKinder > 0) {
    items.push({
      id: 'freibetraege_kinder',
      beschreibung: 'Freibeträge bei Kapitalerträgen bei Kindern',
      summe: freibetraegeKinder,
      verwendungszweck: 'Steuerfreie Kapitalerträge für Kinder',
      popup_text: 'Jedes Kind hat einen eigenen Freibetrag von 12.640€ × 26,375% Abgeltungsteuer.',
      type: 'numeric',
    });
  }

  // 4. Kindererziehungszeiten (§55 SGB VI)
  const foerderhoeheKiz = berechneFoerderhoeheKinder(data.kinder);
  if (foerderhoeheKiz > 0) {
    items.push({
      id: 'kindererziehungszeiten',
      beschreibung: 'Kindererziehungszeiten §55 SGB VI',
      summe: foerderhoeheKiz,
      verwendungszweck: 'Rentenversicherungsbeiträge für Kindererziehungszeiten',
      popup_text: `Pro Kind werden ${KINDERERZIEHUNG_JE_KIND_MONATLICH.toFixed(2).replace('.', ',')}€ monatlich für die Rentenversicherung gutgeschrieben.`,
      type: 'numeric',
    });
  }

  // 5. Vermoegenswirksame Leistungen (AG-Beitrag)
  const vlResult = berechneVermoegenswirksameLeistungen(data.berufsstand1, data.berufsstand2);
  if (vlResult.agBeitrag > 0) {
    items.push({
      id: 'vl_ag',
      beschreibung: 'Vermögenswirksame Leistungen',
      summe: vlResult.agBeitrag,
      verwendungszweck: 'Arbeitgeberzuschuss für vermögenswirksame Leistungen',
      popup_text: 'Angestellte, Auszubildende und Beamte erhalten bis zu 480€ jährlich vom Arbeitgeber.',
      type: 'numeric',
    });
    total += vlResult.agBeitrag;
  }

  // 6. Kirchensteuer + Basisrente (nur wenn Bruttogehalt eingegeben — PHP-konform)
  let kirchensteuerData: KirchensteuerResult | null = null;
  let kirchensteuerPartnerData: KirchensteuerResult | null = null;

  if (data.bruttogehalt1) {
    kirchensteuerData = berechneKirchensteuer(data.bruttogehalt1 * 12, data.kinder, data.plz);
    const basisrenteVorteil = kirchensteuerData.foerdersummeBasisrente;
    if (basisrenteVorteil > 0) {
      items.push({
        id: 'basisrente',
        beschreibung: 'Basisrente (§10 Abs. 1 Nr. 2b EStG)',
        summe: basisrenteVorteil,
        verwendungszweck: 'Steuerliche Förderung der Basisrente (Rürup)',
        popup_text: 'Die Basisrente wird mit dem persönlichen Steuersatz gefördert.',
        type: 'numeric',
      });
      total += basisrenteVorteil;
    }
  }

  if (data.bruttogehalt2) {
    kirchensteuerPartnerData = berechneKirchensteuer(data.bruttogehalt2 * 12, data.kinder, data.plz);
  }

  // Custom: Halbeinkuenfteverfahren
  if (data.sparvertraege === 'ja') {
    items.push({
      id: 'halbeinkuenfte',
      beschreibung: 'Halbeinkünfteverfahren für Kapitalerträge',
      summe: 'Muss individuell berechnet werden',
      verwendungszweck: 'Steuervorteil bei Kapitallebensversicherungen',
      popup_text: 'Bei Auszahlung nach 12 Jahren und ab 62. Lebensjahr wird nur die Hälfte der Erträge versteuert.',
      type: 'custom',
    });
  }

  // Kredit-Foerderungen (if immobilie !== 'Nein')
  if (data.immobilie !== 'Nein') {
    items.push(
      {
        id: 'kfw_300',
        beschreibung: 'Förderkredit für Familien (KfW Programm 300)',
        summe: 'Zinsgünstige Förderung',
        verwendungszweck: 'Neubau oder Ersterwerb von klimafreundlichem Wohnraum für Familien',
        popup_text: 'Bis zu 270.000€ Kreditbetrag mit vergünstigtem Zinssatz.',
        type: 'kredit',
      },
      {
        id: 'kfw_297_298',
        beschreibung: 'Förderkredit für den Erwerb (KfW Programm 297,298)',
        summe: 'Zinsgünstige Förderung',
        verwendungszweck: 'Neubau oder Kauf eines klimafreundlichen Gebäudes',
        popup_text: 'Bis zu 150.000€ Kreditbetrag.',
        type: 'kredit',
      },
      {
        id: 'kfw_261',
        beschreibung: 'Förderung für effiziente Gebäude (KfW Programm 261)',
        summe: 'Zinsgünstige Förderung',
        verwendungszweck: 'Sanierung zum Effizienzhaus oder Einzelmaßnahmen',
        popup_text: 'Bis zu 150.000€ Kreditbetrag mit Tilgungszuschuss.',
        type: 'kredit',
      },
      {
        id: 'kfw_124',
        beschreibung: 'Förderkredit für Bau oder Kauf (KfW Programm 124)',
        summe: 'Zinsgünstige Förderung',
        verwendungszweck: 'Bau oder Kauf einer selbstgenutzten Immobilie',
        popup_text: 'Bis zu 100.000€ Kreditbetrag.',
        type: 'kredit',
      },
    );
  }

  const foerderhoehe = berechneFoerderhoeheKinder(data.kinder);

  return {
    total,
    foerderhoehe,
    items,
    kirchensteuerData,
    kirchensteuerPartnerData,
  };
}

// Pool-Routing: bis 2026-05-27 als matchPoolRouting hier (FE-seitig).
// Decision #61: Pool-Routing laeuft jetzt ueber POST /api/callcenter/route-lead;
// matchPoolRouting + routing_rules-Spalte entfernt.
